import React, { useState, useMemo } from 'react';
import { 
  ShieldExclamationIcon, 
  PlusIcon, 
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { Tab } from '@headlessui/react';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { useWarranties } from '../hooks/useWarranties';
import { useOrganizations } from '../hooks/useOrganizations';
import LoadingScreen from '../components/common/LoadingScreen';
import WarrantyForm from '../components/warranty/WarrantyForm';
import WarrantyClaimForm from '../components/warranty/WarrantyClaimForm';
import WarrantyDocumentGenerator from '../components/warranty/WarrantyDocumentGenerator';
import { CreateWarrantyData, CreateWarrantyClaimData } from '../types/warranty';
import type { Warranty as WarrantyType } from '../types/warranty';
import toast from 'react-hot-toast';

const Warranty: React.FC = () => {
  const { organizations, loading: orgsLoading } = useOrganizations();
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [isWarrantyFormOpen, setIsWarrantyFormOpen] = useState(false);
  const [isClaimFormOpen, setIsClaimFormOpen] = useState(false);
  const [isDocumentGeneratorOpen, setIsDocumentGeneratorOpen] = useState(false);
  const [selectedWarranty, setSelectedWarranty] = useState<WarrantyType | null>(null);
  
  // Use the first organization by default
  const currentOrgId = selectedOrgId || organizations[0]?.id;
  const { warranties, claims, stats, loading, refresh, createWarranty, createWarrantyClaim } = useWarranties(currentOrgId);

  const tabs = [
    { name: 'Áttekintés', icon: ChartBarIcon },
    { name: 'Garanciák', icon: ShieldExclamationIcon },
    { name: 'Garanciális esetek', icon: ExclamationTriangleIcon }
  ];

  // Filter warranties for quick stats
  const warrantyStats = useMemo(() => {
    if (!warranties.length) return null;
    
    const now = new Date();
    const in30Days = new Date();
    in30Days.setDate(now.getDate() + 30);
    
    return {
      total: warranties.length,
      active: warranties.filter(w => w.status === 'active').length,
      expired: warranties.filter(w => w.status === 'expired').length,
      expiringSoon: warranties.filter(w => 
        w.status === 'active' && w.endDate <= in30Days && w.endDate > now
      ).length
    };
  }, [warranties]);

  const handleCreateWarranty = async (data: CreateWarrantyData) => {
    try {
      await createWarranty(data);
      toast.success('Garancia sikeresen létrehozva!');
      setIsWarrantyFormOpen(false);
    } catch (error) {
      console.error('Error creating warranty:', error);
      toast.error('Hiba történt a garancia létrehozásakor');
    }
  };

  const handleCreateClaim = async (data: CreateWarrantyClaimData) => {
    try {
      await createWarrantyClaim(data);
      toast.success('Garanciális eset sikeresen létrehozva!');
      setIsClaimFormOpen(false);
    } catch (error) {
      console.error('Error creating warranty claim:', error);
      toast.error('Hiba történt a garanciális eset létrehozásakor');
    }
  };

  const handleGenerateDocument = (warranty: WarrantyType) => {
    setSelectedWarranty(warranty);
    setIsDocumentGeneratorOpen(true);
  };

  if (orgsLoading) {
    return <LoadingScreen />;
  }

  if (!organizations.length) {
    return (
      <div className="w-full max-w-full flex-grow px-2 sm:px-4 py-3 sm:py-6 space-y-4">
        <CommonPageHeader
          title="Garancia"
          subtitle="Garanciális esetek és jótállási információk kezelése"
          icon={ShieldExclamationIcon}
        />
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 text-center">
          <p className="text-gray-600 dark:text-gray-400">Nincs elérhető szervezet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full flex-grow px-2 sm:px-4 py-3 sm:py-6 space-y-4">
      <CommonPageHeader
        title="Garancia"
        subtitle="Garanciális esetek és jótállási információk kezelése"
        icon={ShieldExclamationIcon}
        onRefresh={refresh}
        refreshButtonText="Frissítés"
      >
        {/* Organization selector */}
        {organizations.length > 1 && (
          <select
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            className="bg-white/20 dark:bg-gray-800/20 backdrop-blur-sm text-white border border-white/30 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm"
          >
            {organizations.map(org => (
              <option key={org.id} value={org.id} className="text-gray-900 dark:text-gray-100">
                {org.name}
              </option>
            ))}
          </select>
        )}
        
        {/* Add warranty button */}
        <button
          onClick={() => setIsWarrantyFormOpen(true)}
          className="bg-white/20 dark:bg-gray-800/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 dark:hover:bg-gray-800/30 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Új garancia
        </button>
      </CommonPageHeader>

      {/* Statistics Cards */}
      {warrantyStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <ShieldExclamationIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Összes garancia</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{warrantyStats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Aktív</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{warrantyStats.active}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                <XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Lejárt</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{warrantyStats.expired}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                <ClockIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Hamarosan lejár</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{warrantyStats.expiringSoon}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
          <Tab.List className="flex space-x-1 rounded-t-lg bg-gray-50 dark:bg-gray-700 p-1">
            {tabs.map((tab, index) => (
              <Tab
                key={tab.name}
                className={({ selected }) =>
                  `w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-gray-700 dark:text-gray-300 transition-all
                   focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-opacity-75
                   ${selected
                     ? 'bg-white dark:bg-gray-600 text-blue-700 dark:text-blue-400 shadow'
                     : 'text-gray-500 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-600/50 hover:text-gray-700 dark:hover:text-gray-300'
                   }`
                }
              >
                <div className="flex items-center justify-center space-x-2">
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </div>
              </Tab>
            ))}
          </Tab.List>
          
          <Tab.Panels className="p-6">
            <Tab.Panel>
              {/* Overview Tab */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Garancia áttekintés</h3>
                
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ShieldExclamationIcon className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-500 mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Garancia áttekintés
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      {warranties.length === 0 
                        ? 'Még nincsenek garanciák regisztrálva.'
                        : `${warranties.length} garancia van regisztrálva.`
                      }
                    </p>
                  </div>
                )}
              </div>
            </Tab.Panel>
            
            <Tab.Panel>
              {/* Warranties Tab */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Garanciák</h3>
                </div>
                
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
                  </div>
                ) : warranties.length === 0 ? (
                  <div className="text-center py-8">
                    <ShieldExclamationIcon className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-500 mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Nincsenek garanciák
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Kezdje el az első garancia regisztrálásával.
                    </p>
                                         <button
                       onClick={() => setIsWarrantyFormOpen(true)}
                      className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-2 rounded-md text-sm flex items-center mx-auto hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                     >
                       <PlusIcon className="h-4 w-4 mr-2" />
                       Új garancia
                     </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {warranties.map(warranty => (
                      <div key={warranty.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-gray-100">{warranty.warrantyNumber}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{warranty.productName}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-500">{warranty.customerName}</p>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              warranty.status === 'active' 
                                ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400'
                                : warranty.status === 'expired'
                                ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400'
                                : 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400'
                            }`}>
                              {warranty.status === 'active' ? 'Aktív' : 
                               warranty.status === 'expired' ? 'Lejárt' : 
                               warranty.status}
                            </span>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              Lejár: {warranty.endDate.toLocaleDateString('hu-HU')}
                            </p>
                            <button
                              onClick={() => handleGenerateDocument(warranty)}
                              className="mt-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm flex items-center"
                            >
                              <DocumentTextIcon className="h-4 w-4 mr-1" />
                              Garanciajegy
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Tab.Panel>
            
            <Tab.Panel>
              {/* Claims Tab */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Garanciális esetek</h3>
                  <button
                    onClick={() => setIsClaimFormOpen(true)}
                    className="bg-orange-600 dark:bg-orange-700 text-white px-4 py-2 rounded-md text-sm flex items-center hover:bg-orange-700 dark:hover:bg-orange-600 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Új eset
                  </button>
                </div>
                
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 dark:border-orange-400"></div>
                  </div>
                ) : claims.length === 0 ? (
                  <div className="text-center py-8">
                    <ExclamationTriangleIcon className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-500 mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Nincsenek garanciális esetek
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      Itt fognak megjelenni a bejelentett garanciális esetek.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {claims.map(claim => (
                      <div key={claim.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-gray-100">{claim.claimNumber}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{claim.description}</p>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              claim.status === 'pending' 
                                ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400'
                                : claim.status === 'completed'
                                ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400'
                                : 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400'
                            }`}>
                              {claim.status === 'pending' ? 'Folyamatban' : 
                               claim.status === 'completed' ? 'Lezárva' : 
                               claim.status}
                            </span>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {claim.reportedDate.toLocaleDateString('hu-HU')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>

      {/* Warranty Form Modal */}
      <WarrantyForm
        isOpen={isWarrantyFormOpen}
        onClose={() => setIsWarrantyFormOpen(false)}
        onSubmit={handleCreateWarranty}
        loading={loading}
      />

      {/* Warranty Claim Form Modal */}
      <WarrantyClaimForm
        isOpen={isClaimFormOpen}
        onClose={() => setIsClaimFormOpen(false)}
        onSubmit={handleCreateClaim}
        loading={loading}
        organizationId={currentOrgId || ''}
      />

      {/* Warranty Document Generator Modal */}
      <WarrantyDocumentGenerator
        isOpen={isDocumentGeneratorOpen}
        onClose={() => setIsDocumentGeneratorOpen(false)}
        warranty={selectedWarranty}
      />
    </div>
  );
};

export default Warranty; 